import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { filter, map, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RegisterKkmFields } from '../register-kkm.model';
import { ModalService } from '../../../../core/modal/modal.service';
import {
  SelectTariffModalComponent,
  SelectTariffModalResult,
} from './select-tariff-modal/select-tariff-modal.component';
import { Tariff, TariffsSearchParams } from '../../../tariffs/tariff.model';
import { ModalWidth } from '../../../../core/modal/modal.model';
import { TariffService } from '../../../../core/services/tariff.service';
import { KkmsApiService } from '../../../../core/modules/kkms/kkms-api.service';
import { RetailPlacesApiService } from '../../../../core/modules/retail-places/retail-places-api.service';
import { RetailPlace } from '../../../../core/modules/retail-places/retail-paces.types';
import { SettingsService } from '../../../../core/services/settings.service';

interface UiKkmModel {
  title: string;
  id: number;
  label?: string;
}

@UntilDestroy()
@Component({
  selector: 'tax-kkm-reg-form',
  templateUrl: './kkm-reg-form.component.html',
  styleUrls: ['./kkm-reg-form.component.sass'],
})

export class KkmRegFormComponent implements OnInit, OnChanges {
  @Input() kkmForm?: RegisterKkmFields;
  @Input() isChangingOfd = false;
  @Output() continueProcess: EventEmitter<RegisterKkmFields> = new EventEmitter<RegisterKkmFields>();
  retailPlaces: RetailPlace[] = [];
  kkmModels: UiKkmModel[] = [];
  isConfirmed = true;
  selectedTariff?: Tariff;

  constructor(
    private router: Router,
    private kkmsApiService: KkmsApiService,
    private modalService: ModalService,
    private tariffService: TariffService,
    private retailPlacesApiService: RetailPlacesApiService,
    private settingsService: SettingsService,
  ) { }

  ngOnInit(): void {
    this.retailPlacesApiService.fetchRetailPlaces()
      .pipe(untilDestroyed(this))
      .subscribe(response => this.retailPlaces = response);
    this.settingsService.getSettings().pipe(
      switchMap(settings => (settings['kkm.registration.isna.enabled'] === 'true'
        ? this.kkmsApiService.fetchKkmIsnaModels()
        : this.kkmsApiService.fetchKkmInisModels()
      )),
      untilDestroyed(this),
    ).subscribe((response: Array<string>) => {
      let firstKkm: UiKkmModel | undefined;
      this.kkmModels = response
        .filter((kkmModelName) => {
          if (kkmModelName === '«E-kassa» (Е-КАССА)') {
            firstKkm = {
              title: kkmModelName, id: 0, label: 'kkm.registration.model.free-kkm',
            };
            return false;
          }
          return true;
        })
        .reduce((kkmModelsList, kkmModelName) => {
          kkmModelsList.push({
            title: kkmModelName, id: kkmModelsList.length,
          });
          return kkmModelsList;
        }, (firstKkm ? [firstKkm] : []));
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('kkmForm' in changes && this.kkmForm?.tariffId) {
      this.tariffService.getPublicTariffs()
        .pipe(
          map(tariffs => tariffs.find(tariff => tariff.id === Number(this.kkmForm?.tariffId))),
          untilDestroyed(this),
        )
        .subscribe((selectedTariff) => {
          this.selectedTariff = selectedTariff;
        });
    }
  }

  // @ts-ignore
  selectedModel(model): void {
    // @ts-ignore
    this.kkmForm.kkmModel = model.title;
  }

  // @ts-ignore
  selectedRetailPlace(retailPlace): void {
    // @ts-ignore
    [this.kkmForm.retailPlaceId, this.kkmForm.retailPlaceTitle] = [retailPlace.id, retailPlace.title];
  }

  isYearValid(year: number): boolean {
    const currentYear: number = new Date().getFullYear();
    return (Boolean(year) && year >= 2015 && year <= currentYear);
  }

  isFormValid(): boolean {
    return Boolean(this.kkmForm?.kkmInternalName
        && this.kkmForm?.kkmModel
        && this.kkmForm?.kkmSerialNumber
        && (this.isChangingOfd ? this.kkmForm.kkmRegId : true)
        && this.isYearValid(this.kkmForm?.kkmYear)
        && this.kkmForm?.retailPlaceId)
        && this.isConfirmed;
  }

  processKkmForm(continueRegistration: boolean): void {
    if (!this.kkmForm) return;
    this.kkmForm.continueRegistration = continueRegistration;
    if (this.isChangingOfd) this.kkmForm.isChangingOfd = true;
    this.continueProcess.emit(this.kkmForm);
  }

  showTariffsModal(): void {
    const searchParams: TariffsSearchParams = {
      kkmModel: this.kkmForm?.kkmModel || '',
      kkmSerial: this.kkmForm?.kkmSerialNumber || '',
    };

    this.modalService.openOld<SelectTariffModalResult>(SelectTariffModalComponent, {
      searchParams,
      kkmProcessFields: this.kkmForm,
    }, {
      width: ModalWidth.md,
    })
      .pipe(
        filter(selectedTariff => Boolean(selectedTariff)),
        untilDestroyed(this),
      )
      .subscribe((selectedTariff) => {
        this.selectedTariff = selectedTariff;
        // @ts-ignore
        this.kkmForm = {
          ...this.kkmForm,
          billingIsAutoRenewableSubscription: true,
          consultantPhone: selectedTariff.consultantPhone,
          kkmConnectionId: selectedTariff.simNumber,
          billingPromoCode: selectedTariff.promoCode,
          tariffId: String(selectedTariff.id),
        };
      });
  }

  back(): void {
    this.router.navigate(['main/kkms']);
  }

  unselectTariff(): void {
    this.selectedTariff = undefined;
    // @ts-ignore
    this.kkmForm = {
      ...this.kkmForm,
      billingPromoCode: '',
      billingIsAutoRenewableSubscription: false,
      kkmConnectionId: undefined,
      tariffId: '0',
    };
  }
}
